import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Download,
    FileSpreadsheet,
    FileText,
    Image as ImageIcon,
    Presentation,
    Video,
    File as FileIcon,
} from 'lucide-react';
import { FILE_MIME_TYPES } from '@nordinsight/implant-core';
import FileModal from '../video-overview/FileModal';
import { getDownloadUrl } from '../../services/api/fileService';
import { useToast } from '../../hooks/use-toast';
import type { FileMember } from '../../services/api/folderService';

const iconForFile = ({ documentType, mimeType }: FileMember) => {
    if (mimeType === FILE_MIME_TYPES.pptx) return Presentation;
    if (mimeType === FILE_MIME_TYPES.xlsx) return FileSpreadsheet;
    switch (documentType) {
        case 'image':
            return ImageIcon;
        case 'video':
            return Video;
        case 'document':
            return FileText;
        default:
            return FileIcon;
    }
};

const PREVIEWABLE_TYPES: FileMember['documentType'][] = [
    'image',
    'video',
    'document',
];

interface FileMemberItemProps {
    file: FileMember;
}

export const FileMemberItem = ({ file }: FileMemberItemProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [isOpening, setIsOpening] = useState(false);
    const downloads = !PREVIEWABLE_TYPES.includes(file.documentType);
    const name = file.title ?? file.filename;
    const [viewerUrl, setViewerUrl] = useState<string | null>(null);
    const Icon = iconForFile(file);

    const handleOpen = async () => {
        setIsOpening(true);
        try {
            const url = await getDownloadUrl(file._id);
            if (!url) throw new Error('Empty download URL');
            if (downloads) {
                window.open(url, '_blank', 'noopener,noreferrer');
            } else {
                setViewerUrl(url);
            }
        } catch {
            toast({
                title: 'Error',
                description: 'Could not open file.',
                variant: 'destructive',
            });
        } finally {
            setIsOpening(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={handleOpen}
                disabled={isOpening}
                aria-label={
                    downloads
                        ? t('folders.detail.downloadAria', { name })
                        : undefined
                }
                className="block w-full text-left disabled:opacity-60"
            >
                <div className="flex items-start gap-2 px-4 py-3 transition-colors hover:bg-muted">
                    <Icon
                        className="mt-0.5 h-4 w-4 shrink-0 text-subtle-foreground"
                        aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                            {name}
                        </div>
                        {file.title && file.title !== file.filename && (
                            <div className="mt-0.5 truncate text-xs font-medium text-subtle-foreground">
                                {file.filename}
                            </div>
                        )}
                    </div>
                    {downloads && (
                        <Download
                            className="mt-0.5 h-4 w-4 shrink-0 text-subtle-foreground"
                            aria-hidden="true"
                        />
                    )}
                </div>
            </button>

            {viewerUrl && (
                <FileModal
                    file={{
                        ...file,
                        url: viewerUrl,
                        title: file.title ?? file.filename,
                    }}
                    close={() => setViewerUrl(null)}
                />
            )}
        </>
    );
};
